import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth, axiosInstance as axios } from '../context/AuthContext';
import {
  FaCheck, FaTimes, FaClock,
  FaUsers, FaCalendarAlt, FaMapMarkerAlt, FaUserPlus, FaEnvelope
} from 'react-icons/fa';
import Header from '../components/Header';
import ShareDialog from '../components/ShareDialog';
import { OverviewTab, TasksTab, BudgetTab, PhotosTab, AttendeesTab } from '../components/events';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

function EventDetails() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [event, setEvent] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [budget, setBudget] = useState(null);
  const [rsvps, setRsvps] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [family, setFamily] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  const [userRsvp, setUserRsvp] = useState(null);
  const [showRsvpModal, setShowRsvpModal] = useState(false);
  const [attendableMembers, setAttendableMembers] = useState({ account_members: [], proxy_members: [] });
  
  const [showEditEvent, setShowEditEvent] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmails, setInviteEmails] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [sendingInvites, setSendingInvites] = useState(false);
  const [editEventData, setEditEventData] = useState({
    name: '',
    start_date: '',
    end_date: '',
    location: '',
    description: '',
    photo_permission: 'all_members'
  });

  useEffect(() => {
    fetchEventData();
  }, [eventId]);

  const fetchEventData = async () => {
    try {
      const [eventRes, tasksRes, rsvpsRes, photosRes] = await Promise.all([
        axios.get(`${API}/events/${eventId}`, { withCredentials: true }),
        axios.get(`${API}/events/${eventId}/tasks`, { withCredentials: true }),
        axios.get(`${API}/events/${eventId}/rsvps`, { withCredentials: true }),
        axios.get(`${API}/events/${eventId}/photos`, { withCredentials: true })
      ]);
      
      setEvent(eventRes.data);
      setTasks(tasksRes.data);
      const rsvpData = rsvpsRes.data.rsvps || rsvpsRes.data;
      setRsvps(rsvpData);
      setPhotos(photosRes.data);
      
      const familyRes = await axios.get(`${API}/families/${eventRes.data.family_id}`, { withCredentials: true });
      setFamily(familyRes.data);
      
      const myRsvp = rsvpData.find(r => r.user_id === user?.user_id && !r.proxy_id);
      setUserRsvp(myRsvp);
      
      try {
        const budgetRes = await axios.get(`${API}/events/${eventId}/budget`, { withCredentials: true });
        setBudget(budgetRes.data);
      } catch (error) {
        setBudget(null);
      }
    } catch (error) {
      console.error('Error fetching event data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendableMembers = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/attendable-members`, { withCredentials: true });
      setAttendableMembers(response.data);
    } catch (error) {
      console.error('Error fetching attendable members:', error);
    }
  };

  const handleRSVP = async (status) => {
    try {
      await axios.post(
        `${API}/rsvp`,
        { event_id: eventId, status },
        { withCredentials: true }
      );
      
      const response = await axios.get(`${API}/events/${eventId}/rsvps`, { withCredentials: true });
      const rsvpData = response.data.rsvps || response.data;
      setRsvps(rsvpData);
      const myRsvp = rsvpData.find(r => r.user_id === user?.user_id && !r.proxy_id);
      setUserRsvp(myRsvp);
    } catch (error) {
      console.error('Error updating RSVP:', error);
      alert('Failed to update RSVP');
    }
  };

  const handleRSVPOnBehalf = async (memberId, memberType, status) => {
    try {
      const payload = { event_id: eventId, status };
      if (memberType === 'proxy') {
        payload.on_behalf_of_proxy_id = memberId;
      } else {
        payload.on_behalf_of_user_id = memberId;
      }
      
      await axios.post(`${API}/rsvp`, payload, { withCredentials: true });
      
      const response = await axios.get(`${API}/events/${eventId}/rsvps`, { withCredentials: true });
      const rsvpData = response.data.rsvps || response.data;
      setRsvps(rsvpData);
      await fetchAttendableMembers();
    } catch (error) {
      console.error('Error updating RSVP:', error);
      alert('Failed to update RSVP');
    }
  };

  const openRsvpModal = () => {
    fetchAttendableMembers();
    setShowRsvpModal(true);
  };
  
  const openEditModal = () => {
    setEditEventData({
      name: event?.name || '',
      start_date: event?.start_date || '',
      end_date: event?.end_date || '',
      location: event?.location || '',
      description: event?.description || '',
      photo_permission: event?.photo_permission || 'all_members'
    });
    setShowEditEvent(true);
  };
  
  const handleEditEvent = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.patch(
        `${API}/events/${eventId}`,
        editEventData,
        { withCredentials: true }
      );
      setEvent(response.data);
      setShowEditEvent(false);
    } catch (error) {
      console.error('Error updating event:', error);
      alert('Failed to update event');
    }
  };
  
  const handleDeleteEvent = async () => {
    try {
      await axios.delete(`${API}/events/${eventId}`, { withCredentials: true });
      navigate(`/family/${event?.family_id}`);
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Failed to delete event');
    }
  };

  const sendInvites = async () => {
    const emails = inviteEmails.split(/[,;\n]/).map(e => e.trim()).filter(e => e);
    if (emails.length === 0) {
      alert('Please enter at least one email address');
      return;
    }
    
    setSendingInvites(true);
    try {
      await axios.post(`${API}/events/${eventId}/send-invites`, {
        event_id: eventId,
        recipient_emails: emails,
        include_ics: true,
        custom_message: inviteMessage || null
      });
      alert(`Invitations sent to ${emails.length} recipient(s)!`);
      setShowInviteModal(false);
      setInviteEmails('');
      setInviteMessage('');
    } catch (err) {
      alert('Failed to send invitations');
    } finally {
      setSendingInvites(false);
    }
  };
  
  const canEditEvent = event?.created_by === user?.user_id || family?.created_by === user?.user_id;

  // Event action handlers for Header component
  const eventActions = {
    onShare: () => setShowShareDialog(true),
    onInvite: () => setShowInviteModal(true),
    onEdit: openEditModal,
    onDelete: () => setShowDeleteConfirm(true),
    canEdit: canEditEvent
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading event details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="event-details">
      {/* Header */}
      <Header 
        variant="event" 
        event={event} 
        family={family} 
        eventActions={eventActions} 
      />

      {/* RSVP Section */}
      <div className="bg-blue-50 border-b border-blue-100">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <p className="text-gray-700 font-medium mb-2">Will you be attending?</p>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">
                  {rsvps.filter(r => r.status === 'attending' || r.response === 'attending').length} attending
                </span>
                <span className="text-gray-400">•</span>
                <span className="text-sm text-gray-600">
                  {rsvps.filter(r => r.status === 'maybe' || r.response === 'maybe').length} maybe
                </span>
                <span className="text-gray-400">•</span>
                <span className="text-sm text-gray-600">
                  {rsvps.filter(r => r.status === 'not_attending' || r.response === 'not_attending').length} not attending
                </span>
              </div>
            </div>
            <div className="flex space-x-2 flex-wrap gap-2">
              <button
                onClick={() => handleRSVP('attending')}
                className={`px-4 py-2 rounded-lg font-medium ${
                  userRsvp?.status === 'attending'
                    ? 'bg-green-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
                data-testid="rsvp-attending-btn"
              >
                <FaCheck className="inline mr-2" />
                Yes
              </button>
              <button
                onClick={() => handleRSVP('maybe')}
                className={`px-4 py-2 rounded-lg font-medium ${
                  userRsvp?.status === 'maybe'
                    ? 'bg-yellow-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
                data-testid="rsvp-maybe-btn"
              >
                <FaClock className="inline mr-2" />
                Maybe
              </button>
              <button
                onClick={() => handleRSVP('not_attending')}
                className={`px-4 py-2 rounded-lg font-medium ${
                  userRsvp?.status === 'not_attending'
                    ? 'bg-red-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
                data-testid="rsvp-not-attending-btn"
              >
                <FaTimes className="inline mr-2" />
                No
              </button>
              <button
                onClick={openRsvpModal}
                className="px-4 py-2 rounded-lg font-medium bg-purple-600 text-white hover:bg-purple-700"
                data-testid="rsvp-for-others-btn"
              >
                <FaUserPlus className="inline mr-2" />
                RSVP for Others
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* RSVP on Behalf Modal */}
      {showRsvpModal && (
        <RSVPOnBehalfModal 
          attendableMembers={attendableMembers}
          onRSVP={handleRSVPOnBehalf}
          onClose={() => setShowRsvpModal(false)}
        />
      )}

      {/* Tabs */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="flex space-x-8 overflow-x-auto">
            <TabButton label="Overview" active={activeTab === 'overview'} onClick={() => setActiveTab('overview')} dataTestId="tab-overview" />
            <TabButton label="Tasks" active={activeTab === 'tasks'} onClick={() => setActiveTab('tasks')} dataTestId="tab-tasks" />
            <TabButton label="Budget" active={activeTab === 'budget'} onClick={() => setActiveTab('budget')} dataTestId="tab-budget" />
            <TabButton label="Photos" active={activeTab === 'photos'} onClick={() => setActiveTab('photos')} dataTestId="tab-photos" />
            <TabButton label="Attendees" active={activeTab === 'attendees'} onClick={() => setActiveTab('attendees')} dataTestId="tab-attendees" />
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="container mx-auto px-4 py-8">
        {activeTab === 'overview' && <OverviewTab event={event} tasks={tasks} rsvps={rsvps} budget={budget} />}
        {activeTab === 'tasks' && <TasksTab tasks={tasks} eventId={eventId} eventName={event?.name} event={event} familyId={family?.family_id} canUseTemplate={canEditEvent} familyMembers={family?.members || []} onTasksUpdated={fetchEventData} />}
        {activeTab === 'budget' && <BudgetTab budget={budget} eventId={eventId} familyId={event?.family_id} familyMembers={family?.members || []} onBudgetUpdated={fetchEventData} />}
        {activeTab === 'photos' && (
          <PhotosTab 
            photos={photos} 
            eventId={eventId}
            familyId={event?.family_id}
            onPhotosUpdated={fetchEventData}
            canUpload={event?.photo_permission === 'all_members' || family?.created_by === user?.user_id || event?.created_by === user?.user_id}
            canDelete={family?.created_by === user?.user_id || event?.created_by === user?.user_id}
          />
        )}
        {activeTab === 'attendees' && <AttendeesTab rsvps={rsvps} />}
      </main>
      
      {/* Edit Event Modal */}
      {showEditEvent && (
        <EditEventModal
          editEventData={editEventData}
          setEditEventData={setEditEventData}
          onSubmit={handleEditEvent}
          onClose={() => setShowEditEvent(false)}
        />
      )}
      
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <DeleteConfirmModal
          eventName={event?.name}
          onConfirm={handleDeleteEvent}
          onClose={() => setShowDeleteConfirm(false)}
        />
      )}

      {/* Share Dialog */}
      <ShareDialog
        isOpen={showShareDialog}
        onClose={() => setShowShareDialog(false)}
        resourceType="event"
        resourceId={eventId}
        familyId={event?.family_id}
        resourceTitle={event?.name}
      />

      {/* Send Invites Modal */}
      {showInviteModal && (
        <SendInvitesModal
          inviteEmails={inviteEmails}
          setInviteEmails={setInviteEmails}
          inviteMessage={inviteMessage}
          setInviteMessage={setInviteMessage}
          sendingInvites={sendingInvites}
          onSend={sendInvites}
          onClose={() => setShowInviteModal(false)}
        />
      )}
    </div>
  );
}

// Helper Components
function TabButton({ label, active, onClick, dataTestId }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-4 border-b-2 font-medium transition whitespace-nowrap ${
        active
          ? 'border-blue-600 text-blue-600'
          : 'border-transparent text-gray-600 hover:text-gray-800'
      }`}
      data-testid={dataTestId}
    >
      {label}
    </button>
  );
}

function RSVPOnBehalfModal({ attendableMembers, onRSVP, onClose }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center px-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 max-h-[80vh] overflow-y-auto" data-testid="rsvp-behalf-modal">
        <h3 className="text-2xl font-bold text-gray-800 mb-4">RSVP on Behalf of Family Members</h3>
        <p className="text-gray-600 mb-4">Mark attendance for family members who can't RSVP themselves.</p>
        
        {/* Account Members */}
        {attendableMembers.account_members.length > 0 && (
          <div className="mb-6">
            <h4 className="text-lg font-semibold text-gray-700 mb-3">Account Members</h4>
            <div className="space-y-3">
              {attendableMembers.account_members.map((member) => (
                <MemberRSVPRow key={member.id} member={member} type="account" onRSVP={onRSVP} />
              ))}
            </div>
          </div>
        )}

        {/* Proxy Members */}
        {attendableMembers.proxy_members.length > 0 && (
          <div className="mb-6">
            <h4 className="text-lg font-semibold text-gray-700 mb-3">Family Members (No Account)</h4>
            <div className="space-y-3">
              {attendableMembers.proxy_members.map((member) => (
                <MemberRSVPRow key={member.id} member={member} type="proxy" onRSVP={onRSVP} isProxy />
              ))}
            </div>
          </div>
        )}

        {attendableMembers.account_members.length === 0 && attendableMembers.proxy_members.length === 0 && (
          <p className="text-gray-500 text-center py-4">No family members to RSVP for.</p>
        )}

        <div className="mt-4 pt-4 border-t border-gray-200">
          <button onClick={onClose} className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

function MemberRSVPRow({ member, type, onRSVP, isProxy = false }) {
  return (
    <div className={`flex items-center justify-between p-3 rounded-lg ${isProxy ? 'bg-purple-50 border border-purple-200' : 'bg-gray-50'}`}>
      <div className="flex items-center space-x-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isProxy ? 'bg-purple-100 text-purple-600' : 'bg-blue-100 text-blue-600'}`}>
          <span className="font-bold">{member.name?.charAt(0)}</span>
        </div>
        <div>
          <p className="font-medium text-gray-800">{member.name}</p>
          {isProxy && member.relationship && <p className="text-sm text-purple-600">{member.relationship}</p>}
          {member.rsvp_status && (
            <p className="text-sm text-gray-500">
              Currently: <span className={member.rsvp_status === 'attending' ? 'text-green-600' : member.rsvp_status === 'maybe' ? 'text-yellow-600' : 'text-red-600'}>{member.rsvp_status}</span>
            </p>
          )}
        </div>
      </div>
      <div className="flex space-x-1">
        <button onClick={() => onRSVP(member.id, type, 'attending')} className={`flex items-center gap-1 px-2 py-1.5 sm:p-2 rounded text-xs sm:text-sm ${member.rsvp_status === 'attending' ? 'bg-green-600 text-white' : 'bg-gray-200 hover:bg-green-100'}`} title="Attending">
          <FaCheck />
          <span className="sm:hidden">Yes</span>
        </button>
        <button onClick={() => onRSVP(member.id, type, 'maybe')} className={`flex items-center gap-1 px-2 py-1.5 sm:p-2 rounded text-xs sm:text-sm ${member.rsvp_status === 'maybe' ? 'bg-yellow-500 text-white' : 'bg-gray-200 hover:bg-yellow-100'}`} title="Maybe">
          <FaClock />
          <span className="sm:hidden">Maybe</span>
        </button>
        <button onClick={() => onRSVP(member.id, type, 'not_attending')} className={`flex items-center gap-1 px-2 py-1.5 sm:p-2 rounded text-xs sm:text-sm ${member.rsvp_status === 'not_attending' ? 'bg-red-600 text-white' : 'bg-gray-200 hover:bg-red-100'}`} title="Not Attending">
          <FaTimes />
          <span className="sm:hidden">No</span>
        </button>
      </div>
    </div>
  );
}

function EditEventModal({ editEventData, setEditEventData, onSubmit, onClose }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center px-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" data-testid="edit-event-modal">
        <h3 className="text-2xl font-bold text-gray-800 mb-4">Edit Event</h3>
        
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-gray-700 font-medium mb-2">Event Name</label>
            <input type="text" value={editEventData.name} onChange={(e) => setEditEventData({...editEventData, name: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600" required data-testid="edit-event-name" />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-700 font-medium mb-2">Start Date</label>
              <input type="date" value={editEventData.start_date} onChange={(e) => setEditEventData({...editEventData, start_date: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600" required data-testid="edit-event-start-date" />
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-2">End Date</label>
              <input type="date" value={editEventData.end_date || ''} onChange={(e) => setEditEventData({...editEventData, end_date: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600" data-testid="edit-event-end-date" />
            </div>
          </div>
          
          <div>
            <label className="block text-gray-700 font-medium mb-2">Location</label>
            <input type="text" value={editEventData.location} onChange={(e) => setEditEventData({...editEventData, location: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600" required data-testid="edit-event-location" />
          </div>
          
          <div>
            <label className="block text-gray-700 font-medium mb-2">Description</label>
            <textarea value={editEventData.description || ''} onChange={(e) => setEditEventData({...editEventData, description: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600" rows="3" data-testid="edit-event-description" />
          </div>
          
          <div>
            <label className="block text-gray-700 font-medium mb-2">Photo Upload Permission</label>
            <select value={editEventData.photo_permission} onChange={(e) => setEditEventData({...editEventData, photo_permission: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600" data-testid="edit-event-photo-permission">
              <option value="all_members">All family members can upload</option>
              <option value="admins_only">Only admins can upload</option>
            </select>
          </div>
          
          <div className="flex space-x-4 pt-4">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-testid="save-event-btn">Save Changes</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DeleteConfirmModal({ eventName, onConfirm, onClose }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center px-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" data-testid="delete-event-modal">
        <h3 className="text-2xl font-bold text-gray-800 mb-4">Delete Event?</h3>
        <p className="text-gray-600 mb-6">
          Are you sure you want to delete <strong>{eventName}</strong>? 
          This action cannot be undone. All tasks, RSVPs, and photos associated with this event will be permanently deleted.
        </p>
        <div className="flex space-x-4">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Cancel</button>
          <button onClick={onConfirm} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700" data-testid="confirm-delete-event-btn">Delete Event</button>
        </div>
      </div>
    </div>
  );
}

function SendInvitesModal({ inviteEmails, setInviteEmails, inviteMessage, setInviteMessage, sendingInvites, onSend, onClose }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-lg w-full p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Send Event Invitations</h2>
        <p className="text-gray-600 mb-4">Invitations will include an ICS calendar file attachment.</p>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Email Addresses (one per line or comma-separated)</label>
          <textarea value={inviteEmails} onChange={(e) => setInviteEmails(e.target.value)} placeholder="email1@example.com&#10;email2@example.com" className="w-full px-3 py-2 border border-gray-300 rounded-lg h-32 focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Personal Message (optional)</label>
          <textarea value={inviteMessage} onChange={(e) => setInviteMessage(e.target.value)} placeholder="Add a personal note to your invitation..." className="w-full px-3 py-2 border border-gray-300 rounded-lg h-20 focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
        </div>
        <div className="flex justify-end space-x-3">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:text-gray-800">Cancel</button>
          <button onClick={onSend} disabled={sendingInvites} className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center space-x-2">
            {sendingInvites ? (
              <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div><span>Sending...</span></>
            ) : (
              <><FaEnvelope /><span>Send Invites</span></>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default EventDetails;
